import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Settings, Network, CheckCircle, BarChart2 } from "lucide-react";

const TelecomUseCase = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <div className="relative bg-[#0077bb] py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative">
            <h1 className="text-4xl font-bold text-white mb-6">
              Telecommunications Solutions
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl">
              Empower your telecom operations with digital solutions designed for efficiency and reliability.
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
          <div>
            <img
              src="https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d"
              alt="Telecommunications infrastructure"
              className="rounded-lg shadow-xl"
            />
          </div>
          <div>
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Enhance Telecom Operations
            </h2>
            <p className="text-lg text-gray-600 mb-6">
              Transform your telecommunications processes with digital solutions that improve efficiency and reliability:
            </p>
            <ul className="space-y-4 text-gray-600">
              <li className="flex items-start">
                <span className="mr-2">•</span>
                Field service management
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                Network infrastructure inspections
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                Equipment maintenance tracking
              </li>
              <li className="flex items-start">
                <span className="mr-2">•</span>
                Compliance and safety documentation
              </li>
            </ul>
          </div>
        </div>

        {/* Key Features Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">
              Key Features
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Settings className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Field Service Management</h3>
                <p className="text-gray-600">
                  Optimize field operations with real-time data and task management.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Network className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Network Monitoring</h3>
                <p className="text-gray-600">
                  Ensure network reliability with proactive monitoring and alerts.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <BarChart2 className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="text-xl font-semibold mb-4">Performance Analytics</h3>
                <p className="text-gray-600">
                  Track KPIs and optimize network performance.
                </p>
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Common Use Cases Section */}
      <div className="bg-gray-50">
        <section className="py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center mb-12">
            Common Use Cases
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-lg shadow">
              <h3 className="text-xl font-semibold mb-4">Infrastructure Inspections</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Tower inspections</li>
                <li>Cable and wiring checks</li>
                <li>Equipment maintenance</li>
                <li>Safety compliance</li>
              </ul>
            </div>
            <div className="bg-white p-8 rounded-lg shadow">
              <h3 className="text-xl font-semibold mb-4">Field Service Operations</h3>
              <ul className="list-disc list-inside space-y-2 text-gray-600">
                <li>Work order management</li>
                <li>Technician dispatch</li>
                <li>Real-time updates</li>
                <li>Service history tracking</li>
              </ul>
            </div>
          </div>
        </section>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* CTA Section */}
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold mb-6">
              Ready to Transform Your Telecom Operations?
            </h2>
            <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
              Join leading telecom companies who have optimized their operations with our digital solutions.
            </p>
            <div className="space-x-4">
              <Link to="/request-demo">
                <Button className="bg-blue-600 text-white px-8 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
                  Request Demo
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border border-blue-600 text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-50 transition-colors">
                  Contact Sales
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TelecomUseCase;
